<template>
  <div :class="cardClasses">
    <div class="flex align-items-center justify-space-between">
      <div class="">
        <div class="flex flex-wrap gap-x-xs align-items-start">
          <div class="flex flex-col gap-y-xxs">
            <div>
              <h3 class="text-semi m-y-none">
                <component :is="forAppEmbed ? 'span' : 'a'"
                          :href="!forAppEmbed ? '/wrestlers/' + wrestlerProfile.id : undefined"
                          target="_blank">
                  {{ wrestlerProfile.full_name }}
                </component>
                <span v-if="saving" class="text-success">
                  <spinner :inline="true"></spinner>
                </span>
                <span v-if="error" class="text-danger">
                  Error saving attendance
                </span>
              </h3>
            </div>
            <div class="text-12">{{ wrestlerSubtitle }}</div>
          </div>
          <div class="flex flex-wrap gap-x-xxs gap-y-xxs items-center">
            <span
              v-if="trialBookingLabel"
              class="text-12 font-semi rounded-xxs p-x-xs p-y-xxs inline-flex align-items-center gap-x-xxs border border-solid bg-yellow-50 text-yellow-800 border-yellow-300"
              :title="`Booked a trial in this class — ${trialBookingLabel}`"
            >
              <i class="fa-regular fa-calendar-check"></i>
              <span>Trial booked</span>
            </span>
            <span
              v-if="classPassLabel"
              :class="classPassChipClasses"
              class="text-12 font-semi rounded-xxs p-x-xs p-y-xxs inline-flex align-items-center gap-x-xxs border border-solid"
              :title="`Class pass used — ${classPassLabel}`"
            >
              <i class="fa-solid fa-ticket"></i>
              <span>{{ classPassLabel }}</span>
            </span>
            <tag
              v-for="tag in rosterTagsWithCount(wrestlerProfile)"
              :tag="tag"
              :key="tag.abbreviation"
              :count="tag.count"
            ></tag>
          </div>
        </div>
        <div v-if="(showRosterFeedback && !onEventRosters) || weeklyLimitReached" class="m-t-xxs flex flex-wrap gap-x-xxs gap-y-xxs align-items-center">
          <span v-if="showRosterFeedback && !onEventRosters" class="text-12 font-semi bg-yellow-50 text-yellow-800 border border-solid border-yellow-300 rounded-xxs p-x-xs p-y-xxs">
            <i class="fa-solid fa-triangle-exclamation"></i> Not on this event's rosters
          </span>
          <span v-if="weeklyLimitReached" class="text-12 font-semi bg-yellow-50 text-yellow-800 border border-solid border-yellow-300 rounded-xxs p-x-xs p-y-xxs">
            <i class="fa-solid fa-triangle-exclamation"></i> Weekly class limit reached
          </span>
        </div>
      </div>
      <div class="check-in-button flex-shrink-0">
        <div v-show="editingStatus && editable">
          <button
            :class="presentClasses"
            @click.prevent="statusTapped('present')"
          >
            <i class="fa-solid fa-check"></i>
          </button>
          <div class="btn-group m-l-xs">
            <button
              type="button"
              class="btn btn-danger btn-outline dropdown-toggle"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <i class="fa-solid fa-xmark"></i> <span class="caret"></span>
            </button>
            <ul class="dropdown-menu system-background">
              <li><a @click.prevent="statusTapped('excused')" class="system-label">Excused</a></li>
              <li>
                <a @click.prevent="statusTapped('unexcused')" class="system-label">Unexcused</a>
              </li>
              <li><a @click.prevent="statusTapped('late')" class="system-label">Late</a></li>
              <li role="separator" class="divider"></li>
              <li><a @click.prevent="statusTapped('injured')" class="system-label">Injured</a></li>
              <li><a @click.prevent="statusTapped('other')" class="system-label">Other</a></li>
            </ul>
          </div>
        </div>
        <div v-show="!editingStatus">
          <div class="">
            <span :class="statusTextStyle">
              <i :class="statusIcon"></i> {{ readableStatus(true) }}
            </span>
            <div class="btn-group link-btn" v-if="editable">
              <button
                data-toggle="dropdown"
                aria-expanded="false"
                class="btn btn-link icon-only dropdown-toggle"
              >
                <i aria-hidden="true" class="fa-solid fa-caret-down"></i>
                <span class="sr-only">Toggle Dropdown</span>
              </button>
              <ul class="dropdown-menu">
                <template v-if="hasCustomQuestions">
                  <li>
                    <a @click.prevent="showQuestions = !showQuestions">
                      {{ showQuestions ? "Hide" : "View" }} additional info...
                    </a>
                  </li>
                  <li role="separator" class="divider"></li>
                </template>
                <li>
                  <a @click.prevent="editingNotes = true">
                    {{ this.checkInNotes ? "Edit Note..." : "Add Note..." }}
                  </a>
                </li>
                <li role="separator" class="divider"></li>
                <li>
                  <a @click.prevent="statusTapped('unknown')">Change...</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div>
      <div v-show="editingNotes" class="w-full m-t-sm">
        <div class="form-group">
          <div class="input-group w-full">
            <label>Note</label>
            <textarea
              v-model="draftNotes"
              class="form-control font-no-ios-zoom"
              rows="2"
              style="resize: none"
              name="draftNotes"
              placeholder="Add notes here.."
            ></textarea>
          </div>
          <div class="grid grid-cols-2 gap-xs sm-grid-cols-1 md-grid-cols-1 m-t-sm">
            <ladda-button
              @lbClicked="save"
              :click-arg="true"
              el-class="btn-primary order-last-lg"
              :loading="saving"
            >
              Save
            </ladda-button>
            <button
              v-show="!saving"
              type="button"
              @click.prevent="editingNotes = false"
              class="btn btn-white"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
      <p class="notes" v-show="checkInNotes && !editingNotes">
        Note: {{ checkInNotes }}
      </p>
    </div>
    <div class="m-t-xs" v-if="showQuestions">
      <div class="m-b-sm" v-html="checkedInAt"></div>
      <registration-info-form
        profile-type="WrestlerProfile"
        :profile-id="wrestlerProfile.id"
        :check-in-id="checkInId"
        :specific-questions="questions"
        button-cta="Save"
        :endpoint="endpoint"
        :access-key="accessKey"
        :cloudfront-base-url="cloudfrontBaseUrl"
      ></registration-info-form>
    </div>
  </div>
</template>

<script>
import Spinner from './spinner.vue';
import LaddaButton from '../../shared/ladda_button.vue';
import { checkInMix } from './mixins/check_in_mix';
import RegistrationInfoForm from '../../shared/registration_info_form.vue';
import Tag from '../../shared/tag.vue';
import { taggableMix } from '../../shared/taggable_mix';

export default {
  name: 'check-in-form',
  components: {
    Tag,
    LaddaButton,
    RegistrationInfoForm,
    Spinner,
  },
  mixins: [checkInMix, taggableMix],
  props: {
    wrestlerProfile: {
      type: Object,
      required: true,
    },
    checkIn: {
      type: Object,
      required: false,
    },
    eventId: {
      type: Number,
      required: true,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    questions: {
      type: Array,
      default: [],
    },
    endpoint: {
      type: String,
      required: false,
    },
    accessKey: {
      type: String,
      required: false,
    },
    cloudfrontBaseUrl: {
      type: String,
      required: false,
    },
    forAppEmbed: {
      type: Boolean,
      default: false,
    },
    // When true, shows which rosters the wrestler is on (or a warning that they are
    // on none of the event's linked rosters) so a coach gets eligibility feedback
    // before marking attendance. Used for wrestlers surfaced outside the roster filter.
    showRosterFeedback: {
      type: Boolean,
      default: false,
    },
    eventRosterIds: {
      type: Array,
      default: () => [],
    },
    // Set to the trial session name when this wrestler booked a trial into this
    // class and hasn't been marked yet. Null once they're marked — the parent
    // owns that decision because it holds the check-ins.
    trialBookingLabel: {
      type: String,
      default: null,
    },
  },
  computed: {
    checkedIn() {
      return this.checkInStatus === 'present' || this.checkInStatus === 'late';
    },
    absentFamily() {
      return ['absent', 'excused', 'unexcused', 'injured', 'other'].includes(this.checkInStatus);
    },
    classPassPaidSession() {
      const pass = this.classPass || this.checkIn?.class_pass;
      return pass?.paid_session || null;
    },
    classPassSessionType() {
      return this.classPassPaidSession?.session_type || null;
    },
    // Name of the pass used at check-in (e.g. "7 day trial"), shown as a chip so
    // pass-based attendees stand out in the unfiltered roll call without having
    // to filter to trial/drop-in. Null when no class pass backs the check-in.
    classPassLabel() {
      return this.classPassPaidSession?.name || null;
    },
    classPassChipClasses() {
      // Soft/outlined style with a ticket icon so the chip reads as a pass, not
      // another roster tag (those are solid, uppercase abbreviations).
      if (this.classPassSessionType === 'trial') {
        return 'bg-green-50 text-green-800 border-green-300';
      }
      if (this.classPassSessionType === 'dropin') {
        return 'bg-blue-50 text-blue-800 border-blue-300';
      }
      return 'bg-gray-100 text-gray-700 border-gray-300';
    },
    // True when checking in to this event would need a weekly membership pass
    // the wrestler doesn't have, mirroring the kiosk's block. The server
    // decides per event (a camp registrant isn't held to their membership's
    // cap). Only surfaced before check-in so it reads as a heads-up, not a
    // post-hoc flag on someone already marked present. Missing summary (e.g.
    // check-in-derived rows without subscription data loaded) yields false.
    weeklyLimitReached() {
      const summary = this.wrestlerProfile?.class_pass_summary;
      return summary?.weekly_limit_reached === true && !this.checkedIn;
    },
    onEventRosters() {
      // An event with no linked rosters has no restrictions
      if (!this.eventRosterIds || this.eventRosterIds.length === 0) {
        return true;
      }

      const rosterIds = _.map(this.wrestlerProfile.rosters || [], 'id');
      return _.intersection(rosterIds, this.eventRosterIds).length > 0;
    },
    cardClasses() {
      return {
        'check-in-card': true,
        'text-muted': this.absentFamily,
        'check-in-card--present': this.checkInStatus === 'present' && !this.classPassSessionType,
        'check-in-card--late': this.checkInStatus === 'late' && !this.classPassSessionType,
        'check-in-card--absent': this.absentFamily,
        'check-in-card--trial': this.checkedIn && this.classPassSessionType === 'trial',
        'check-in-card--dropin': this.checkedIn && this.classPassSessionType === 'dropin',
      };
    },
    wrestlerSubtitle() {
      const weightClass = this.wrestlerProfile.weight_class || 'Unknown weight';
      const academicClass = this.wrestlerProfile.academic_class || '';
      // "105." reads like list numbering — show "105 lbs · 1st grade" instead.
      // Only append lbs to numeric weight classes (not 'HWT'/'Unknown weight').
      const weightLabel = Number.isNaN(Number(weightClass)) ? weightClass : `${weightClass} lbs`;
      if (!academicClass) return weightLabel;
      return `${weightLabel} · ${academicClass}`;
    },
    statusTextStyle() {
      return {
        'text-primary': this.present,
        'text-warning': this.excused,
        'text-danger': this.absent,
      };
    },
    hasCustomQuestions() {
      return this.questions?.length > 0;
    },
    checkedInAt() {
      if (this.createdAt) {
        const time = moment(this.createdAt).format('M/D/YYYY, hh:mm a');
        return `Marked <strong>${_.lowerCase(
          this.readableStatus(),
        )}</strong> at ${time}`;
      }

      return 'Has not checked in';
    },
  },
  data() {
    // see CheckInMix for majority of data
    return {
      editingNotes: false,
      checkInNotes: '',
      draftNotes: '',
      showQuestions: false,
      classPass: this.checkIn?.class_pass || null,
    };
  },
  created() {
    // CheckInMix also fires a syncing of props to data
    this.syncNotePropsToData();
    this.$notificationManager.on(
      'registration-info-saved',
      this.questionsSaved,
    );
  },
  mounted() {
    if (!this.editable) {
      this.editingStatus = false;
    }
  },
  destroyed() {
    this.$notificationManager.off(
      'registration-info-saved',
      this.questionsSaved,
    );
  },
  methods: {
    readableStatus(removePresent) {
      const status = this.checkInStatus.replace('_', ' ');

      if (status === 'present' && removePresent) {
        return '';
      }

      // 'unknown' means no attendance recorded yet. Coaches never see this text
      // (they get the check-in buttons); only non-editable viewers (parents) hit
      // this path, so show them a plain "Not checked in" instead of "Unknown".
      if (status === 'unknown') {
        return 'Not checked in';
      }

      return _.capitalize(status);
    },
    capitalize(string) {
      return _.capitalize(string);
    },
    statusTapped(status) {
      const vm = this;
      if (!vm.editable) {
        return;
      }

      vm.checkInStatus = status;
      vm.save(false);
    },
    syncNotePropsToData() {
      if (this.checkIn) {
        this.checkInNotes = this.checkIn.notes || '';
        this.draftNotes = this.checkIn.notes || '';
      }
    },
    save: _.throttle(function (forNote) {
      const vm = this;

      if (vm.saving) {
        return;
      }

      vm.saving = true;
      vm.editingStatus = false;
      const params = {
        check_in: {
          wrestler_profile_id: vm.wrestlerProfileId,
          status: vm.checkInStatus,
          notes: vm.draftNotes,
        },
      };

      if (vm.checkInId) {
        vm.update(params, forNote);
      } else {
        vm.create(params, forNote);
      }
    }, 500),
    update(params, forNote) {
      const vm = this;
      const url = vm.$apiService.eventCheckInUrl(vm.eventId, vm.checkInId);
      axios
        .put(url, params)
        .then((response) => {
          vm.successHandler(response.data, forNote);
        })
        .catch((error) => {
          vm.errorHandler(error);
          vm.editingStatus = true;
        });
    },
    create(params, forNote) {
      const vm = this;
      const url = vm.$apiService.checkInsUrl(vm.eventId);
      axios
        .post(url, { ...params, source: 'coach_calendar' })
        .then((response) => {
          vm.successHandler(response.data, forNote);
        })
        .catch((error) => {
          vm.errorHandler(error);
          vm.editingStatus = true;
        });
    },
    errorHandler(error) {
      const vm = this;
      vm.saving = false;
      vm.error = true;
    },
    successHandler(data, forNote) {
      const vm = this;
      if (data.id) {
        vm.checkInId = data.id;
        vm.createdAt = data?.created_at;
        vm.classPass = data?.class_pass;
      }
      vm.checkInNotes = vm.draftNotes;

      vm.$notificationManager.emit(
        'wrestler-checked-in',
        vm.checkInId,
        vm.checkInStatus,
        vm.wrestlerProfile,
        vm.checkInNotes,
        vm.classPass,
      );
      let toastText = `${vm.wrestlerProfile.full_name} marked as ${_.lowerCase(
        vm.readableStatus(false),
      )}`;
      if (forNote) {
        toastText = `Note for ${vm.wrestlerProfile.full_name} saved`;
      }
      vm.$notificationManager.emit('show-toast', toastText, true);
      vm.saving = false;
      vm.editingNotes = false;
      if (vm.checkInStatus === 'unknown' && vm.editable) {
        vm.editingStatus = true;
      } else if (!forNote && this.hasCustomQuestions) {
        vm.showQuestions = true;
      }
    },
    questionsSaved(profileId, profileType) {
      if (
        profileType === 'WrestlerProfile'
        && profileId === this.wrestlerProfile?.id
      ) {
        this.showQuestions = false;
      }
    },
  },
};
</script>
